import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { useToast } from '@sketch/toasts'
import {
  ConfirmationDialog,
  ModalInjectedProps,
  RadioButton,
} from '@sketch/components'
import { DeletePreviousShareVersionsMutationVariables } from '@sketch/gql-types'
import { pluralize } from '@sketch/utils'
import { usePreviousVersionsDelete } from '../../operations'

type PreserveVersionsOption = NonNullable<
  DeletePreviousShareVersionsMutationVariables['preserveVersions']
>

export interface DeletePreviousVersionsModalProps extends ModalInjectedProps {
  shareIdentifiers: string[]
  shareName?: string
  versionIdentifier?: string
  skipNavigation?: boolean
  onDeleted?: () => void
}

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`

export const DeletePreviousVersionsModal: React.FC<
  DeletePreviousVersionsModalProps
> = ({
  hideModal,
  isModalOpen,
  shareIdentifiers,
  shareName,
  versionIdentifier,
  skipNavigation,
  onDeleted,
}) => {
  const { showToast } = useToast()
  const [preserveVersions, setPreserveVersions] =
    useState<PreserveVersionsOption>('STARRED_ONLY')

  // Snapshot shareIdentifiers at mount time — the parent may recompute
  // this array to [] after the mutation updates the Apollo cache, before
  // onCompleted fires.
  const shareIdentifiersSnapshot = useRef(shareIdentifiers)

  const [deletePreviousVersions, { loading }] = usePreviousVersionsDelete({
    shareIdentifiers,
    versionIdentifier,
    preserveVersions,
    skipNavigation,
    onError: message => {
      showToast(message, 'negative')
    },
    onCompleted: deletedCount => {
      const versionText = pluralize('version', 'versions', deletedCount)
      const snapshotLength = shareIdentifiersSnapshot.current.length
      const subject = shareName
        ? `"${shareName}"`
        : `${snapshotLength} ${pluralize('document', 'documents', snapshotLength)}`
      showToast(`${deletedCount} ${versionText} of ${subject} deleted`)
      hideModal()
      onDeleted?.()
    },
  })

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPreserveVersions(event.target.value as PreserveVersionsOption)
  }

  const bodyText = shareName ? (
    <p>
      This will delete all{' '}
      {versionIdentifier ? (
        <>
          versions of &ldquo;<strong>{shareName}</strong>&rdquo; before this
          version
        </>
      ) : (
        <>
          previous versions of &ldquo;<strong>{shareName}</strong>&rdquo;
        </>
      )}
      . You can&apos;t undo this action.
    </p>
  ) : (
    <p>
      This will delete all previous versions of{' '}
      <strong>
        {shareIdentifiers.length}{' '}
        {pluralize('document', 'documents', shareIdentifiers.length)}
      </strong>{' '}
      from your selection that{' '}
      {pluralize('has', 'have', shareIdentifiers.length)} multiple versions. You
      can&apos;t undo this action.
    </p>
  )

  return (
    <ConfirmationDialog
      title="Delete Previous Versions?"
      hideModal={hideModal}
      isModalOpen={isModalOpen}
      cancelButton={{
        disabled: loading,
      }}
      confirmButton={{
        text: 'Delete Versions',
        variant: 'negative',
        loading,
        disabled: loading,
      }}
      onConfirm={() => deletePreviousVersions()}
    >
      {bodyText}
      <RadioGroup>
        <RadioButton
          name="preservation-option"
          value="STARRED_ONLY"
          checked={preserveVersions === 'STARRED_ONLY'}
          onChange={handleOptionChange}
          label="Preserve starred versions"
          help="Delete older versions but keep starred versions"
        />
        <RadioButton
          name="preservation-option"
          value="STARRED_OR_WITH_DESCRIPTION"
          checked={preserveVersions === 'STARRED_OR_WITH_DESCRIPTION'}
          onChange={handleOptionChange}
          label="Preserve starred versions and versions with descriptions"
          help="Delete older versions but keep starred versions and versions with descriptions"
        />
        <RadioButton
          name="preservation-option"
          value="NONE"
          checked={preserveVersions === 'NONE'}
          onChange={handleOptionChange}
          label="Delete all previous versions"
          help={
            versionIdentifier
              ? 'Only keep this version and newer versions'
              : 'Only keep the latest version'
          }
        />
      </RadioGroup>
    </ConfirmationDialog>
  )
}
